<template>
  <div class="location-search">
    <ThemedSearch
      ref="searchInput"
      v-model="searchQuery"
      placeholder="Search for a location..."
      aria-label="Search for a location"
      @keydown.enter.native="performSearch"
      @focus.native="onFocus"
      @blur.native="onBlur"
      @clear="clearSearch"
    />

    <!-- Search Results Dropdown -->
    <transition name="dropdown-fade">
      <div
        v-if="showResults && results.length > 0"
        class="search-results"
      >
        <button
          v-for="(result, index) in results"
          :key="index"
          type="button"
          class="result-item"
          @click="selectResult(result, index)"
          @mouseenter="hoveredIndex = index"
          @mouseleave="hoveredIndex = null"
        >
          <ThemeIcon icon="location" type="icons" classes="result-icon" />
          <div class="result-content">
            <div class="result-name">
              {{ result.name }}
            </div>
            <div class="result-address">
              {{ result.formatted_address }}
            </div>
          </div>
        </button>
      </div>
    </transition>

    <!-- No Results Message -->
    <transition name="dropdown-fade">
      <div
        v-if="showResults && searchQuery && results.length === 0 && !loading"
        class="no-results"
      >
        <p>No locations found</p>
      </div>
    </transition>

    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-indicator">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { debounce } from './utils/mapHelpers.js';
import ThemeIcon from '../ThemeIcon.vue';
import ThemedSearch from '../ThemedSearch.vue';

export default {
  name: 'LocationSearch',

  components: {
    ThemeIcon,
    ThemedSearch,
  },

  props: {
    initialValue: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      searchQuery: '',
      results: [],
      rawResults: [], // Store raw Google Maps results
      showResults: false,
      loading: false,
      hoveredIndex: null,
      geocoder: null,
      debouncedSearch: null
    };
  },

  watch: {
    searchQuery(newValue) {
      if (newValue.length >= 3) {
        this.debouncedSearch();
      } else {
        this.results = [];
        this.showResults = false;
      }
    },
    
    initialValue: {
      handler(newValue) {
        if (newValue && newValue !== this.searchQuery) {
          this.searchQuery = newValue;
        }
      },
      immediate: true
    }
  },

  mounted() {
    // Wait for Google Maps to load before initializing geocoder
    this.initializeGeocoder();

    // Create debounced search function
    this.debouncedSearch = debounce(this.searchLocations.bind(this), 300);

    // Close results when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    /**
     * Initialize Google Maps Places Service
     * Waits for Google Maps to be available
     */
    initializeGeocoder() {
      const checkGoogleMaps = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          // Create a hidden div for the PlacesService (required by Google Maps API)
          const tempMapDiv = document.createElement('div');
          tempMapDiv.style.display = 'none';
          document.body.appendChild(tempMapDiv);
          
          // Create a temporary map for the PlacesService
          const tempMap = new google.maps.Map(tempMapDiv, {
            center: { lat: 39.8283, lng: -98.5795 }, // Center of USA
            zoom: 4
          });
          
          // Initialize PlacesService
          this.geocoder = new google.maps.places.PlacesService(tempMap);
          console.log('Google Maps Places Service initialized successfully');
        } else {
          console.log('Waiting for Google Maps to load...');
          setTimeout(checkGoogleMaps, 100);
        }
      };
      
      checkGoogleMaps();
    },

    /**
     * Perform search when Enter is pressed
     */
    performSearch() {
      if (this.results.length > 0) {
        // If we have results, select the first one
        this.selectResult(this.results[0]);
      } else if (this.searchQuery.length >= 3) {
        // Otherwise, trigger a search
        this.searchLocations();
      }
    },

    /**
     * Search for locations using Google Places API
     */
    async searchLocations() {
      if (!this.geocoder) {
        console.warn('Google Maps Places Service not initialized');
        return;
      }
      
      if (!this.searchQuery) {
        return;
      }

      this.loading = true;
      console.log('Searching for:', this.searchQuery);

      // First search - general query
      const request = {
        query: this.searchQuery.trim()
      };

      this.geocoder.textSearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
          console.log('Places API results (first search):', results);

          // If we only got 1-2 results, try a US-specific search to get more options
          if (results.length < 3 && !this.searchQuery.includes('USA') && !this.searchQuery.includes('United States')) {
            this.performUSSearch(results);
          } else {
            this.processResults(results);
          }
        } else {
          console.error('Places search failed:', status);
          this.loading = false;
          this.results = [];
          this.showResults = false;
        }
      });
    },

    /**
     * Perform a US-specific search to get more results
     */
    performUSSearch(existingResults) {
      const usQuery = `${this.searchQuery.trim()}, USA`;
      console.log('Performing US-specific search:', usQuery);

      const usRequest = {
        query: usQuery
      };

      this.geocoder.textSearch(usRequest, (usResults, usStatus) => {
        if (usStatus === google.maps.places.PlacesServiceStatus.OK && usResults && usResults.length > 0) {
          console.log('Places API results (US search):', usResults);
          
          // Combine results - US results first, then existing results
          const combinedResults = [...usResults, ...existingResults];
          
          // Remove duplicates based on formatted_address
          const uniqueResults = combinedResults.filter((result, index, self) => 
            index === self.findIndex(r => r.formatted_address === result.formatted_address)
          );
          
          this.processResults(uniqueResults);
        } else {
          // Fall back to original results if US search fails
          this.processResults(existingResults);
        }
      });
    },

    /**
     * Process and format search results
     */
    processResults(results) {
      this.loading = false;

      // Store raw results for later use
      this.rawResults = results.slice(0, 5);

      // Format results for display only
      this.results = this.rawResults.map(result => ({
        name: result.name,
        formatted_address: result.formatted_address,
        type: this.getLocationType(result)
      }));

      console.log('Final formatted results:', this.results);
      
      this.showResults = true;
    },

    /**
     * Get location type from geocoding result
     */
    getLocationType(result) {
      const types = result.types || [];
      
      if (types.includes('street_address')) return 'Address';
      if (types.includes('locality')) return 'City';
      if (types.includes('administrative_area_level_1')) return 'State';
      if (types.includes('administrative_area_level_2')) return 'County';
      if (types.includes('postal_code')) return 'ZIP Code';
      if (types.includes('country')) return 'Country';
      
      return 'Location';
    },

    /**
     * Get appropriate zoom level based on location type
     */
    getZoomLevel(result) {
      const types = result.types || [];
      
      if (types.includes('street_address')) return 16;
      if (types.includes('locality')) return 12;
      if (types.includes('administrative_area_level_2')) return 10;
      if (types.includes('administrative_area_level_1')) return 7;
      if (types.includes('postal_code')) return 13;
      if (types.includes('country')) return 5;
      
      return 10;
    },

    /**
     * Select a search result
     * Uses the EXACT same pattern as the old LocationSearch component
     */
    selectResult(result, index) {
      // Get the raw Google Maps result (not the Vue reactive one)
      const rawResult = this.rawResults[index];
      
      if (!rawResult || !rawResult.geometry || !rawResult.geometry.location) {
        console.error('Invalid location result');
        return;
      }

      // Extract coordinates directly from Google Maps result
      const location = rawResult.geometry.location;
      const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
      const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

      console.log('Emitting location-selected with:', { lat, lng });

      // Emit in the format expected by theme v1 MapPage
      // (lat/lng at top level, not nested in coordinates)
      this.$emit('location-selected', {
        lat: lat,
        lng: lng,
        zoom: 12, // Fixed zoom level like the old implementation
        name: rawResult.name,
        address: rawResult.formatted_address
      });

      // Keep the selected location in the search input (don't clear it)
      this.searchQuery = rawResult.formatted_address || rawResult.name;
      this.showResults = false;
    },

    /**
     * Clear search input
     */
    clearSearch() {
      this.searchQuery = '';
      this.results = [];
      this.showResults = false;
    },

    /**
     * Handle input focus
     */
    onFocus() {
      if (this.results.length > 0) {
        this.showResults = true;
      }
    },

    /**
     * Handle input blur
     */
    onBlur() {
      // Delay hiding results to allow click events to fire
      // Increased delay to ensure click registers
      setTimeout(() => {
        this.showResults = false;
      }, 300);
    },

    /**
     * Handle clicks outside the component
     */
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showResults = false;
      }
    }
  }
};
</script>
